import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Sliders, Moon, Timer, Utensils, Heart, Dumbbell, UtensilsCrossed, Brain, PersonStanding } from 'lucide-react';

export default function AdminScoring() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get('/scoring-config').then(r => setCfg(c => c || r.data)); }, []);
  if (!cfg) return <div className="text-muted-foreground">Loading…</div>;

  const set = (group, key, val) => setCfg(c => ({ ...c, [group]: { ...c[group], [key]: Math.max(0, Math.min(100, Number(val) || 0)) } }));
  const sum = (group) => (group === 'training' ? Number(cfg.training.gym) + Number(cfg.training.walking) : Object.values(cfg[group]).reduce((a, b) => a + Number(b), 0));

  const save = async () => {
    if (sum('recovery') !== 100 || sum('regen') !== 100 || sum('training') !== 100) { toast.error('Each group must total exactly 100%'); return; }
    if ((cfg.training.brisk_target || 0) < 1000) { toast.error('Brisk walk target must be at least 1,000 steps'); return; }
    setSaving(true);
    try { const r = await api.put('/scoring-config', { recovery: cfg.recovery, regen: cfg.regen, training: cfg.training }); setCfg(r.data); toast.success('Scoring weights saved'); }
    catch { toast.error('Failed to save'); } finally { setSaving(false); }
  };

  const recoveryFields = [
    { key: 'sleep', label: 'Sleep', icon: Moon, color: '#A78BFA' },
    { key: 'fasting', label: 'Fasting Duration', icon: Timer, color: '#00C8FF' },
    { key: 'meal_timing', label: 'Meal Timing', icon: Utensils, color: '#FFB800' },
    { key: 'activities', label: 'Recovery Activities', icon: Heart, color: '#65A30D' },
  ];
  const regenFields = [
    { key: 'training', label: 'Training', icon: Dumbbell, color: '#00C8FF' },
    { key: 'nutrition', label: 'Nutrition', icon: UtensilsCrossed, color: '#65A30D' },
    { key: 'recovery', label: 'Recovery', icon: Heart, color: '#A78BFA' },
    { key: 'mindset', label: 'Mindset', icon: Brain, color: '#FFB800' },
  ];
  const trainingFields = [
    { key: 'gym', label: 'Gym sessions', icon: Dumbbell, color: '#00C8FF' },
    { key: 'walking', label: 'Brisk walk', icon: PersonStanding, color: '#65A30D' },
  ];

  return (
    <div className="space-y-5 max-w-2xl" data-testid="admin-scoring">
      <div>
        <div className="label-mini text-[#00C8FF]">Settings</div>
        <h1 className="font-display font-black text-3xl lg:text-4xl mt-1 flex items-center gap-3"><Sliders className="w-7 h-7 text-[#00C8FF]" /> Scoring Weights</h1>
        <p className="text-muted-foreground text-sm mt-1">Tune how the YourReGen Protocol rewards habits. Each group must total 100%.</p>
      </div>

      <WeightGroup title="Recovery Score Formula" fields={recoveryFields} cfg={cfg} group="recovery" set={set} total={sum('recovery')} testid="recovery" />
      <WeightGroup title="YourReGen Score Formula" fields={regenFields} cfg={cfg} group="regen" set={set} total={sum('regen')} testid="regen" />
      <WeightGroup title="Training Score — days with a gym session" fields={trainingFields} cfg={cfg} group="training" set={set} total={sum('training')} testid="training">
        <p className="text-xs text-muted-foreground mt-4">On days with no scheduled gym session the brisk walk counts for 100%. Several sessions on one day share the gym part equally. Partly finished sessions and walks earn partial credit.</p>
        <label className="flex items-center justify-between mt-4 text-sm">
          <span className="flex items-center gap-2"><PersonStanding className="w-4 h-4 text-[#65A30D]" /> Brisk walk target (steps)</span>
          <input type="number" min="1000" step="500" value={cfg.training.brisk_target} data-testid="training-brisk-target"
            onChange={e => setCfg(c => ({ ...c, training: { ...c.training, brisk_target: e.target.value === '' ? '' : Math.max(0, parseInt(e.target.value, 10) || 0) } }))}
            className="w-24 bg-[var(--c-film)] border border-[var(--c-hair)] rounded-lg px-2 py-1 font-mono text-right focus:outline-none focus:border-[#00C8FF]" />
        </label>
      </WeightGroup>

      <Button onClick={save} disabled={saving} data-testid="save-scoring-btn" className="bg-[#00C8FF] text-[#081726] font-bold w-full sm:w-auto px-8">
        {saving ? 'Saving…' : 'Save Weights'}
      </Button>
    </div>
  );
}

function WeightGroup({ title, fields, cfg, group, set, total, testid, children }) {
  const ok = total === 100;
  return (
    <div className="regen-card-elevated p-5" data-testid={`scoring-${testid}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="font-display font-bold text-lg">{title}</div>
        <span className={`pill ${ok ? 'bg-[var(--c-green-soft)] text-[var(--c-green-ink)]' : 'bg-[#FF5C5C]/10 text-[#FF5C5C]'}`} data-testid={`total-${testid}`}>{total}%</span>
      </div>
      <div className="space-y-4">
        {fields.map(f => (
          <div key={f.key}>
            <div className="flex items-center justify-between mb-1.5">
              <span className="flex items-center gap-2 text-sm"><f.icon className="w-4 h-4" style={{ color: f.color }} />{f.label}</span>
              <div className="flex items-center gap-1">
                <input type="number" min="0" max="100" value={cfg[group][f.key]} onChange={e => set(group, f.key, e.target.value)} data-testid={`weight-${group}-${f.key}`}
                  className="w-16 bg-[var(--c-film)] border border-[var(--c-hair)] rounded-lg px-2 py-1 font-mono text-right focus:outline-none focus:border-[#00C8FF]" />
                <span className="text-muted-foreground text-sm">%</span>
              </div>
            </div>
            <div className="h-1.5 bg-[var(--c-film)] rounded-full overflow-hidden"><div className="h-full rounded-full" style={{ width: `${cfg[group][f.key]}%`, background: f.color }} /></div>
          </div>
        ))}
      </div>
      {!ok && <div className="text-xs text-[#FF5C5C] mt-3">Total must equal 100% (currently {total}%).</div>}
      {children}
    </div>
  );
}
